import React, { useState, useEffect } from 'react';
import { Grid, Button, Container, Typography, CircularProgress, Box, useTheme, Modal } from '@mui/material';
import { styled } from 'styled-components';
import ReactApexChart from 'react-apexcharts';
import SearchIcon from '@mui/icons-material/Search';
import { useLocation } from 'react-router-dom';

// Filtro Popover import
import PopoverFiltroLeads from './PopoverFiltroLeads';
// Modal do Novo Lead
import NewSaleForm from '../NewLead/NewSaleForm';
import EditPropostaLeads from '../EditProposta/EditPropostaLeads';
import ModalExportLeads from './ModalExportLeads';
import AddProposta from '../../AddProposta';
import { updateProposta, GetDadosHomeLeads } from '../../api';
import { cores, coresGrafico } from '../../../styles/styleUtils';

const PageContainer = styled(Box)`
  background-color: ${cores.cinzaClaro};
  min-height: 100vh;
  padding: 32px;
`;

const SummaryPill = styled(Box)`
  background-color: ${cores.chumbo};
  border-radius: 30px;
  padding: 10px 8px;
  color: white;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  margin: 6px;
  min-width: 120px;
`;

const ChartWrapper = styled(Box)`
  padding: 24px;
  height: 100%;
`;

function LeadsDashboardHome(props) {
  const theme = useTheme();

  const dataAtual = new Date();
  const primeiroDiaDoMes = new Date(dataAtual.getFullYear(), dataAtual.getMonth(), 1).toISOString().split('T')[0];
  const ultimoDiaDoMes = new Date(dataAtual.getFullYear(), dataAtual.getMonth() + 1, 0).toISOString().split('T')[0];
  const currentLocation = useLocation();
  const usuarioLogado = JSON.parse(localStorage.getItem('userData'))

  const initialStateFiltro = {
    dataInicio: primeiroDiaDoMes,
    dataFim: ultimoDiaDoMes,
    usuarios: currentLocation.pathname === '/leads' && !usuarioLogado.supervisor ? [usuarioLogado._id] : [],
    unidades: [],
    etapas: []
  };


  const [filtro, setFiltro] = useState(initialStateFiltro);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [loading, setLoading] = useState(false); // Can be set true while fetching API
  const [openNewSale, setOpenNewSale] = useState(false);
  const [openEditProposta, setOpenEditProposta] = useState(false);
  const [openExport, setOpenExport] = useState(false);
  const [produto, setProduto] = useState(null);
  const [open, setOpen] = useState(false);

  // Mocked state for API integration later
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await GetDadosHomeLeads(filtro);
        if (result) {
          setDashboardData(result);
        }
      } catch (error) {
        console.error("Error fetching dashboard data", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filtro]);

  const handleOpenFiltro = (event) => setPopoverAnchor(event.currentTarget);
  const handleCloseFiltro = () => setPopoverAnchor(null);

  const handleConfirmFiltro = (novosFiltros) => {
    setFiltro(novosFiltros);
    handleCloseFiltro();
  };

  const handleLimparFiltro = () => {
    setFiltro(initialStateFiltro);
    handleCloseFiltro();
  };

  const onConfirmNewSale = async (data) => {
    const response = await updateProposta(data);
    setOpenNewSale(false);

    if (response && response.data) {
      setProduto({ ...response.data });
      setOpenEditProposta(true);
    }
  };

  const onConfirmEditProposta = async (data) => {
    const response = await updateProposta(data);
    if (response && response.data) {
      if (response.data.finalizadoLead?.nome === 'Lead Qualificado') {
        setProduto({ ...response.data });
        setOpen(true);
      }
    }
    setOpenEditProposta(false);
  };

  const onConfirmHandler = async (data, anexo) => {
    await updateProposta(data, anexo);
    setProduto(null);
    setOpen(false);
  };



  if (loading || !dashboardData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  // --- Chart Configurations ---
  const funilOptions = {
    chart: { id: 'funil-chart', type: 'bar', toolbar: { show: false }, background: 'transparent' },
    plotOptions: { bar: { horizontal: true, barHeight: '70%', borderRadius: 0 } },
    xaxis: { categories: dashboardData.funil.categories },
    colors: [cores.laranja],
    dataLabels: { enabled: true, style: { fontSize: '14px', colors: [cores.branco] } },
    grid: { show: true, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } }
  };

  const statusOptions = {
    chart: { id: 'status-chart', type: 'pie', toolbar: { show: false }, background: 'transparent' },
    labels: dashboardData.status.labels,
    colors: coresGrafico,
    dataLabels: { enabled: true, dropShadow: { enabled: false }, style: { colors: [cores.chumbo], fontSize: '16px', fontWeight: 'bold' } },
    legend: { position: 'bottom', fontSize: '14px' },
    plotOptions: { pie: { customScale: 1 } }
  };

  const desqualificadosOptions = {
    chart: { id: 'desqualificados-chart', type: 'bar', toolbar: { show: false }, background: 'transparent' },
    plotOptions: { bar: { horizontal: true, barHeight: '70%', borderRadius: 0 } },
    xaxis: { categories: dashboardData.desqualificados.categories },
    colors: [cores.cinzaMedio],
    dataLabels: { enabled: true, style: { fontSize: '14px', colors: [cores.branco] } },
    grid: { show: true, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } }
  };

  const perdidosOptions = {
    chart: { id: 'perdidos-chart', type: 'bar', toolbar: { show: false }, background: 'transparent' },
    plotOptions: { bar: { horizontal: true, barHeight: '70%', borderRadius: 0 } },
    xaxis: { categories: dashboardData.perdidos.categories },
    colors: ['#D32F2F'],
    dataLabels: { enabled: true, style: { fontSize: '14px', colors: [cores.branco] } },
    grid: { show: true, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } }
  };

  return (
    <PageContainer>
      {/* Top Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" fontWeight="bold">Leads</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            color="primary"
            sx={{ borderRadius: 5, px: 4, textTransform: 'none' }}
            onClick={() => setOpenExport(true)}
          >
            Exportar Relatório
          </Button>
          {
            currentLocation.pathname === '/leads' && (
              <Button
                variant="contained"
                sx={{ bgcolor: cores.laranja, '&:hover': { bgcolor: cores.laranjaEscuro }, textTransform: 'none', borderRadius: 5, px: 3 }}
                onClick={() => setOpenNewSale(true)}
              >
                Novo Lead
              </Button>
            )
          }
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={handleOpenFiltro}
            sx={{ textTransform: 'none', borderRadius: 5, px: 3 }}
          >
            Filtros
          </Button>
        </Box>
      </Box>

      {/* Summary Pills */}
      <Box display="flex" flexWrap="wrap" justifyContent="space-between" mb={6}>
        {dashboardData.summary.map((item, index) => (
          <SummaryPill key={index}>
            <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.2, fontSize: '0.8rem' }}>{item.label}</Typography>
            <Typography variant="subtitle1" sx={{ mt: 0.5, fontWeight: 'bold' }}>{item.value}</Typography>
          </SummaryPill>
        ))}
      </Box>

      {/* Charts Grid */}
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <ChartWrapper>
            <Typography variant="h6" fontWeight="bold" align="center" mb={2}>Funil de Leads</Typography>
            <ReactApexChart
              options={funilOptions}
              series={[{ name: 'Leads', data: dashboardData.funil.data }]}
              type="bar"
              height={300}
            />
          </ChartWrapper>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartWrapper>
            <Typography variant="h6" fontWeight="bold" align="center" mb={2}>Status Leads</Typography>
            <Box display="flex" justifyContent="center">
              <ReactApexChart
                options={statusOptions}
                series={dashboardData.status.data}
                type="pie"
                height={300}
                width="100%"
              />
            </Box>
          </ChartWrapper>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartWrapper>
            <Typography variant="h6" fontWeight="bold" align="center" mb={2}>Leads Desqualificados</Typography>
            <ReactApexChart
              options={desqualificadosOptions}
              series={[{ name: 'Leads', data: dashboardData.desqualificados.data }]}
              type="bar"
              height={300}
            />
          </ChartWrapper>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartWrapper>
            <Typography variant="h6" fontWeight="bold" align="center" mb={2}>Projetos Perdidos</Typography>
            <ReactApexChart
              options={perdidosOptions}
              series={[{ name: 'Projetos', data: dashboardData.perdidos.data }]}
              type="bar"
              height={300}
            />
          </ChartWrapper>
        </Grid>
      </Grid>


      {/* Filter Popover */}
      {
        popoverAnchor && (
          <PopoverFiltroLeads
            open={popoverAnchor}
            onClose={handleCloseFiltro}
            onConfirm={handleConfirmFiltro}
            onLimpar={handleLimparFiltro}
            filtro={filtro}
          />
        )
      }

      {/* New Lead Modal */}
      {
        openNewSale && (
          <NewSaleForm
            open={openNewSale}
            handleClose={() => setOpenNewSale(false)}
            onConfirm={onConfirmNewSale}
          />
        )
      }

      {
        openEditProposta && (
          <Modal
            open={openEditProposta}
            onClose={() => setOpenEditProposta(false)}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"

          >
            <EditPropostaLeads
              open={openEditProposta}
              onClose={() => setOpenEditProposta(false)}
              onConfirm={onConfirmEditProposta}
              proposta={produto}
            />
          </Modal>
        )
      }

      {
        open && <AddProposta open={open} proposta={produto} onClose={() => setOpen(false)} onConfirm={onConfirmHandler} />
      }

      {
        openExport && (
          <ModalExportLeads
            open={openExport}
            onClose={() => setOpenExport(false)}
            dashboardData={dashboardData}
            currentFilters={filtro}
          />
        )
      }
    </PageContainer>
  );
}

export default LeadsDashboardHome;

